import { BaseService } from './baseService';
import { 
  CompanyConfiguration,
  CreateCompanyConfigurationRequest,
  UpdateCompanyConfigurationRequest,
  CompanyConfigurationHistoryResponse,
  CompanyInfo,
  ContactInfo
} from '@/types/company-config';

export class CompanyConfigService extends BaseService {
  /**
   * Get the current active company configuration
   */
  async getCompanyConfiguration(): Promise<CompanyConfiguration> {
    const response = await this.api.get('/company/config');
    return response.data;
  }

  /**
   * Create a new company configuration
   */
  async createCompanyConfiguration(data: CreateCompanyConfigurationRequest): Promise<CompanyConfiguration> {
    const response = await this.api.post('/company/config', data);
    return response.data;
  }

  /**
   * Update an existing company configuration
   */
  async updateCompanyConfiguration(
    id: string, 
    data: UpdateCompanyConfigurationRequest
  ): Promise<CompanyConfiguration> {
    const response = await this.api.put(`/company/config/${id}`, data);
    return response.data;
  }

  /**
   * Get company configuration history
   */
  async getCompanyConfigurationHistory(
    page: number = 1, 
    limit: number = 10
  ): Promise<CompanyConfigurationHistoryResponse> {
    const response = await this.api.get('/company/config/history', {
      params: { page, limit }
    });
    return response.data;
  }

  /**
   * Delete a company configuration
   */
  async deleteCompanyConfiguration(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/company/config/${id}`);
    return response.data;
  }

  /**
   * Validate company configuration data
   */
  validateCompanyInfo(companyInfo: CompanyInfo): string[] {
    const errors: string[] = [];
    
    if (!companyInfo.name || companyInfo.name.trim() === '') {
      errors.push('Company name is required');
    }
    
    if (!companyInfo.legalName || companyInfo.legalName.trim() === '') {
      errors.push('Legal company name is required');
    }
    
    if (companyInfo.ein && !/^[0-9]{2}-[0-9]{7}$/.test(companyInfo.ein)) {
      errors.push('EIN must be in format XX-XXXXXXX');
    }
    
    if (companyInfo.website && !this.isValidUrl(companyInfo.website)) {
      errors.push('Website must be a valid URL');
    }
    
    return errors;
  }

  /**
   * Validate contact information
   */
  validateContactInfo(contactInfo: ContactInfo): string[] {
    const errors: string[] = [];
    
    if (!contactInfo.email || !this.isValidEmail(contactInfo.email)) {
      errors.push('Valid email address is required');
    }
    
    if (contactInfo.supportEmail && !this.isValidEmail(contactInfo.supportEmail)) {
      errors.push('Support email must be a valid email address');
    }
    
    if (contactInfo.phone && !/^\+?[1-9]\d{1,14}$/.test(contactInfo.phone)) {
      errors.push('Phone number format is invalid');
    }
    
    if (contactInfo.address) {
      if (!contactInfo.address.street) {
        errors.push('Street address is required');
      }
      if (!contactInfo.address.city) {
        errors.push('City is required');
      }
      if (!contactInfo.address.state) {
        errors.push('State is required');
      }
      if (!contactInfo.address.zipCode || !/^[0-9]{5}(-[0-9]{4})?$/.test(contactInfo.address.zipCode)) {
        errors.push('Valid ZIP code is required (XXXXX or XXXXX-XXXX)');
      }
    }
    
    return errors;
  }

  /**
   * Helper method to validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Helper method to validate URL format
   */
  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }
}

export const companyConfigService = new CompanyConfigService();